import {jwtDecode} from "jwt-decode";
import React, {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {BounceRole, PartnerDomain} from "../../consumer/declarations";
import {useLocalStorageState} from "../../hooks";
import {getTokenExpired, isValidUserId, JwtPayloadType} from "./auth-utils";

export interface AuthContextProps {
  loggedIn: boolean;
  token?: string;
  setToken: (credentials: string | undefined) => void;
  userRole?: BounceRole;
  userId?: string;
  partnerDomain?: string;
  setCredentials: (credentials: UserCredentials) => void;
  /**
   * @deprecated This is a workaround for support login without a user id - please do not use this elsewhere!
   */
  setUserId: (userId: string) => void;
  clearCredentials: () => void;
  isAgent: boolean;
  agentEmail?: string;
  agentRole?: BounceRole;
  isAnonymous: boolean;
  tokenExpired?: boolean;
  credentials?: UserCredentials;
}

export interface UserCredentials {
  token: string;
  userId?: string;
  agentEmail?: string;
  agentRole?: BounceRole;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

const AuthProvider = ({children}: PropsWithChildren) => {
  const {search} = useLocation();
  const urlOrigin = window.location.origin;
  const queryParams = new URLSearchParams(search);
  const [userId, setUserId] = useState<string>();
  const [credentials, setCredentialsInternal] = useLocalStorageState<UserCredentials | undefined>(
    "credentials",
    undefined,
    {encrypted: true}
  );
  const [token, setToken] = useState<string | undefined>(credentials?.token);
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [partnerDomain, setPartnerDomain] = useState<string>();

  useEffect(() => {
    // todo fp: should get by domain
    const substrings = ["3006", "fp", "happymoney"];
    const isHappyMoney = substrings.some((str) => urlOrigin.includes(str));
    const currPartnerDomain = isHappyMoney ? PartnerDomain.HAPPY_MONEY : PartnerDomain.BOUNCE_AI;
    setPartnerDomain(currPartnerDomain);
  }, []);

  const loggedIn = useMemo<boolean>(() => !!credentials, [credentials]);
  const isAgent = useMemo<boolean>(
    () => !!credentials?.token && !credentials?.userId && !!credentials?.agentEmail,
    [credentials]
  );

  const decodedToken: JwtPayloadType | undefined = useMemo(() => {
    if (token) return jwtDecode(token);
  }, [token]);

  const tokenExpired = getTokenExpired(decodedToken);

  const userRole: BounceRole | undefined = useMemo(() => {
    if (decodedToken && !tokenExpired) return decodedToken.role as BounceRole;
  }, [decodedToken, tokenExpired]);

  const agentEmail = useMemo<string | undefined>(() => credentials?.agentEmail, [credentials]);
  const agentRole = useMemo<BounceRole | undefined>(() => {
    return credentials?.agentRole;
  }, [credentials]);
  const setCredentials = useCallback(
    (credentials: UserCredentials): void => {
      setCredentialsInternal(credentials);
    },
    [setCredentialsInternal]
  );

  const clearCredentials = useCallback((): void => {
    setCredentialsInternal(undefined);
  }, [setCredentialsInternal]);

  useEffect(() => {
    if (!credentials?.userId || credentials.agentEmail) return;

    if (!isValidUserId(credentials.userId)) {
      clearCredentials();
      return;
    }

    setUserId(credentials.userId);
  }, [credentials, clearCredentials]);

  useEffect(() => {
    const isAnonymousUser = !Boolean(queryParams.get("id"));
    if (isAnonymousUser) {
      setIsAnonymous(true);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        loggedIn,
        token,
        setToken,
        userRole,
        agentEmail,
        agentRole,
        isAgent,
        userId,
        setUserId,
        setCredentials,
        clearCredentials,
        isAnonymous,
        tokenExpired,
        credentials,
        partnerDomain,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);

  if (!context) throw new Error("AuthContext must be used inside AuthProvider");

  return context;
};

export {AuthContext, AuthProvider, useAuth};
